import {
  IOrder,
  IOrderSummary,
  OrderStatus,
  PagedRequest,
  PagedResponse,
} from '@topshots/core-models';
import { topshotsAPI } from '../../app/api';

const ordersApi = topshotsAPI.injectEndpoints({
  endpoints: (build) => ({
    getOrders: build.query<
      PagedResponse<IOrder[]>,
      PagedRequest & { status?: OrderStatus; search?: string }
    >({
      query: (request) => ({
        url: 'orders',
        method: 'GET',
        params: request,
      }),
      providesTags: (result, error, page) =>
        result
          ? [
              // Provides a tag for each enquiry in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ _id }) => ({
                type: 'Orders' as const,
                id: _id.toString(),
              })),
              { type: 'Orders', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Orders', id: 'PARTIAL-LIST' }],
    }),
    getOrdersCount: build.query<
      { count: number },
      { status?: OrderStatus } | void
    >({
      query: (request) => ({
        url: 'orders/count',
        method: 'GET',
        params: request || undefined,
      }),
      providesTags: [{ type: 'Orders', id: 'PARTIAL-LIST' }],
    }),
    getOrder: build.query<IOrder, string>({
      query: (id) => ({
        url: `orders/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, page) => [
        { type: 'Orders' as const, id: result?._id.toString() },
      ],
    }),
    getOrderTotal: build.query<{ total: number }, string>({
      query: (id) => ({
        url: `orders/${id}/total`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [
        { type: 'OrderTotals' as const, id },
      ],
    }),
    getOrderSummary: build.query<IOrderSummary, string>({
      query: (id) => ({
        url: `orders/${id}/summary`,
        method: 'GET',
      }),
      providesTags: (result, error, page) => [
        { type: 'OrderSummaries' as const, id: result?._id.toString() },
      ],
    }),
    generateOrderItemDownload: build.mutation<
      {
        tid: string;
      },
      { id: string; itemId: string }
    >({
      query: (request) => ({
        url: `orders/${request.id}/items/${request.itemId}/download`,
        method: 'POST',
        body: {},
      }),
    }),
    sendOrderConfirmationEmail: build.mutation<
      {
        success: boolean;
      },
      { id: string; to: string }
    >({
      query: (request) => ({
        url: `orders/${request.id}/email`,
        method: 'POST',
        body: {
          to: request.to,
        },
      }),
    }),
    updateOrder: build.mutation<IOrder, { id: string; data: Partial<IOrder> }>({
      query: (request) => ({
        url: `orders/${request.id}`,
        method: 'PUT',
        body: request.data,
      }),
      invalidatesTags: (result, error, req) => [
        { type: 'Orders', id: 'PARTIAL-LIST' },
        { type: 'Orders', id: result?._id.toString() },
        { type: 'OrderTotals', id: req.id },
        { type: 'OrderSummaries', id: req.id },
      ],
    }),
  }),
});

export const {
  useGetOrderQuery,
  useGetOrdersQuery,
  useGetOrdersCountQuery,
  useUpdateOrderMutation,
  useGetOrderSummaryQuery,
  useGenerateOrderItemDownloadMutation,
  useSendOrderConfirmationEmailMutation,
  useGetOrderTotalQuery,
} = ordersApi;
